import {
  WalletDeclinedError,
  WalletRejectedError,
} from '../../services/Wallet.service';
import type { tThresholdBlockedBy, tWalletCurrency } from '../../Wallet.types';
import { thresholdBoundText } from './TopUpModal.threshold';

/**
 * Clasifica un error de cobro del top-up, en un solo lugar (RET-1933).
 *
 * POR QUÉ EXISTE. `errorMessage`, en el contenedor, cargaba dos clases de
 * hecho distintas bajo el mismo `string`: LO CAPTURADO (un monto que el
 * cliente escribió mal, o que el back rechazó por sí mismo) y CÓMO SALIÓ EL
 * COBRO (qué contestó el banco, o si la red ni siquiera llegó). El botón
 * Continuar apagaba con `Boolean(errorMessage)` a secas, así que un rechazo
 * del banco — donde reintentar es EXACTAMENTE lo que corresponde — bloqueaba
 * igual que un monto inválido, donde reintentar sin cambiar nada da lo mismo.
 *
 * Sin React, sin red: aquí vive la DECISIÓN, no el mecanismo — mismo reparto
 * que `Wallet.outcome.ts` y `Wallet.settlement.ts`. El `switch` es exhaustivo
 * contra `never` por la misma razón que el de `Wallet.outcome.ts`: agregar una
 * clase a la unión sin darle fila aquí no compila.
 *
 * NO INVENTA TAXONOMÍA. Las dos señales que distinguen las clases —
 * `WalletDeclinedError` (402 tipado) e `isAmountRejection` (400, movido aquí
 * desde `TopUpModal.connected.tsx`, mismo cuerpo) — ya existían y ya elegían
 * la copia; lo único nuevo es que la MISMA clasificación decide también si el
 * botón se apaga, en vez de que el contenedor pregunte `Boolean(errorMessage)`
 * sin mirar de qué clase es.
 *
 * DECISIONES DE PRODUCTO CONGELADAS (ver ESTADO de RET-1933):
 * 1. Un 402 habilita Continuar SIEMPRE, sin mirar `retryableWithSameCard`. El
 *    selector de tarjeta del modal está inerte (RET-1923, abierto), así que
 *    condicionar en `false` recrearía el callejón sin salida que este ticket
 *    existe para quitar — y la llave de idempotencia rota en TODO 402
 *    (`clearAttemptId`, en `TopUpModal.connected.tsx`), así que el reintento
 *    siempre llega al banco.
 * 2. Un desenlace DESCONOCIDO (5xx, red caída, error sin `response`) también
 *    habilita Continuar. Es seguro precisamente porque ahí la llave se
 *    CONSERVA (la rotación es asimétrica, ver `Wallet.attempt.ts` y el
 *    comentario de `submit()`), así que Stripe deduplica el reintento en vez
 *    de cobrar dos veces. Esta decisión es SOLO sobre lo desconocido: no
 *    extiende a un 4xx que el upstream sí contestó (ver `isUnknownOutcome`).
 *
 * DOS LLAMADORES, Y LA DECISIÓN 2 NO SE SOSTIENE IGUAL EN LOS DOS. Este módulo
 * lo usa `submit()` (el cobro) y también el `catch` de `enableRecurring()` (el
 * alta de recarga automática). El argumento de arriba —"la llave se conserva,
 * así que Stripe deduplica"— es EXCLUSIVO del cobro: el alta no lleva llave de
 * idempotencia, porque no cobra nada (da de alta un renglón que se factura en
 * la siguiente renovación). Ahí la conclusión sigue siendo la misma, pero por
 * otra razón: repetir el alta no puede duplicar un cobro, y si el alta anterior
 * SÍ había entrado, wallet-service contesta 409 ("workspace already has an
 * active recurring top-up"), que es de la clase que SÍ apaga el botón. O sea
 * que el reintento se auto-limita. La conducta de ese camino está fijada en
 * `TopUpModal.recurringError.test.tsx`.
 *
 * EL 424 ES AMBIGUO Y AQUÍ SE RESUELVE HACIA "REINTENTABLE". `bff-web` no
 * relaya los 401/403 de wallet-service —hablan de NUESTRA llave interna, no de
 * la sesión del cliente— y los convierte en 424; los 5xx tampoco se relayan y
 * salen igual (`relayableUpstreamStatus`, `src/wallet/wallet.service.go`). Así
 * que un 424 mezcla "nuestra llave está rota" (no se arregla reintentando) con
 * "el upstream se cayó" (transitorio), y desde el front no hay forma de
 * separarlos. Se clasifica como desconocido porque el caso transitorio es el
 * común y porque el costo de equivocarse es un viaje de más, no un callejón.
 * Lo que hay que saber: si la llave interna se rompe, TODOS los clientes ven el
 * genérico con el botón vivo hasta que Ops la arregle. El arreglo de fondo no
 * vive aquí — sería que el 424 llevara un discriminador en el cuerpo.
 *
 * Lo que SIGUE bloqueando: un 400 (`amountRejected`), y — desde que
 * bloqueó-por-error se separó de es-desconocido — cualquier otro 4xx que
 * `wallet-service`/`bff-web` haya devuelto con una respuesta real. El caso que
 * lo obligó: el 409 `frozen` de `topup_controller.go` (wallet-service),
 * relayado intacto por `relayableUpstreamStatus` (bff-web,
 * `src/wallet/wallet.service.go`) — un rechazo DEFINITIVO (el workspace sólo
 * sale reactivando el plan o hablando con soporte) que caía en el mismo cajón
 * que "no sé qué pasó" y encendía Continuar sobre un bucle que nunca cierra.
 * `invalidAmount` (sin monto que cobrar) nunca pasa por este clasificador: es
 * una validación LOCAL que el contenedor arma directo con
 * `blocksContinue: true`, porque ahí no hay error de red que clasificar.
 */

export type tTopUpChargeErrorMessage = {
  /** El texto que el cliente lee en el pie del modal. */
  text: string;
  /**
   * Si ESTE mensaje debe apagar Continuar.
   *
   * Vive PEGADO al texto, en el mismo objeto, y no como un booleano aparte
   * que el contenedor derive por su cuenta: son dos estados que tienen que
   * moverse siempre juntos, y dos estados que deben moverse juntos son dos
   * estados que un día se separan (ver el comentario de `errorMessage` en
   * `TopUpModal.connected.tsx`, que ya documenta esa lección para otro caso).
   */
  blocksContinue: boolean;
  /**
   * Si ESTE mensaje deja de ser cierto en cuanto el cliente guarda otra
   * tarjeta (RET-2348).
   *
   * Sólo un rechazo del banco lo es: habla de LA TARJETA con la que se intentó
   * cobrar, y el cobro del wallet sale siempre de la predeterminada, que es
   * justo la que cambia al guardar una desde este modal. Quien sigue la
   * instrucción del rechazo («intenta con otro método de pago») y guarda otra
   * tarjeta no puede seguir leyendo que su cobro falló: con la tarjeta nueva
   * nadie ha intentado cobrar todavía.
   *
   * Un monto rechazado, una caída de red o un rechazo definitivo NO hablan de
   * la tarjeta. Guardar otra no los vuelve falsos, así que se quedan — y con
   * ellos su `blocksContinue`, que es lo que mantiene el botón apagado con la
   * razón a la vista.
   *
   * Pegado al texto por lo mismo que `blocksContinue`: quien lo lee no vuelve
   * a clasificar el error para saber de qué clase era.
   */
  clearsOnNewCard: boolean;
};

/**
 * True for a 400 from the charge endpoint: the server rejected the REQUEST,
 * not the card (402) and not an outage (5xx, red caída).
 *
 * Only the STATUS is read. El cuerpo del 400 todavía no viene estructurado
 * (punto 6 de RET-1895), así que no hay nada más en qué ramificar — y casar
 * por el texto del mensaje se rompería en cuanto alguien reescriba la frase.
 *
 * OJO CON EL NOMBRE, y con lo que el copy puede prometer: el servidor también
 * contesta 400 cuando el CUERPO va mal formado, que es un bug nuestro y no un
 * monto malo del cliente (el desajuste micros-vs-decimal que documenta
 * Wallet.charge.contract.test.ts llegó a producción por esa vía). Desde fuera
 * los dos casos son el mismo 400, así que el mensaje tiene que ser verdadero
 * en LOS DOS: dice que ese cobro no se pudo procesar y que revise el monto o
 * escriba a soporte, sin afirmarle que el error es suyo. Cuando el 400 traiga
 * su código, esta función se parte en dos y cada mitad recupera su copy
 * exacto.
 */
export function isAmountRejection(error: unknown): boolean {
  const response = (error as { response?: { status?: number } })?.response;

  return response?.status === 400;
}

/**
 * True para un desenlace del que de verdad no sabemos nada: la red ni
 * contestó (sin `response` — DNS, timeout, conexión caída), o el status es de
 * los que el propio contrato del upstream reserva para "algo NUESTRO se
 * rompió", no para "tu petición está mal":
 *
 *   - `>= 500`: el upstream se cayó. Ver el 503 `ErrFreezeStateUnknown` de
 *     wallet-service — falló CERRADO (no cobró), así que reintentar es lo
 *     peor que puede pasar si Stripe deduplica, y lo mejor si no.
 *   - `424`: así llega a este front TODO lo que bff-web no puede relayar tal
 *     cual — un 5xx real de wallet-service, o un fallo de transporte del
 *     propio BFF (`upstreamFailure`, `apiErrors.CommunicationError`) — ver
 *     `relayableUpstreamStatus` en `bff-web/src/wallet/wallet.service.go`.
 *
 * Cualquier OTRO status con `response` —409, 404, 412...— es el upstream
 * CONTESTANDO, no cayéndose: `relayableUpstreamStatus` existe justo para que
 * ese status sobreviva el salto por el BFF sin aplastarse, y wallet-service ya
 * decidió que esa respuesta no se arregla reintentando (el 409 `frozen` de
 * `topup_controller.go` sólo se resuelve reactivando el plan o hablando con
 * soporte). Meterlo en el mismo cajón que "no sé qué pasó" sería la misma
 * promesa falsa que el 400 ya evita, sobre un endpoint que mueve dinero.
 */
function isUnknownOutcome(error: unknown): boolean {
  const response = (error as { response?: { status?: number } })?.response;

  if (!response) return true;

  return response.status === undefined || response.status >= 500 || response.status === 424;
}

/**
 * Las cuatro clases que un error de cobro puede ser, cada una con lo que
 * necesita para armar su propia frase — mismo criterio que `tTopUpOutcome` en
 * `Wallet.types.ts`: la unión carga el dato, quien la lee no vuelve a
 * extraerlo de `error` con un cast.
 */
type tClassifiedChargeError =
  | { kind: 'decline'; reason: string }
  | { kind: 'amountRejected' }
  | { kind: 'transportFailure' }
  | { kind: 'definitiveRejection' };

function classify(error: unknown): tClassifiedChargeError {
  if (error instanceof WalletDeclinedError) {
    return { kind: 'decline', reason: error.decline.reason };
  }

  if (isAmountRejection(error)) return { kind: 'amountRejected' };

  if (isUnknownOutcome(error)) return { kind: 'transportFailure' };

  return { kind: 'definitiveRejection' };
}

/**
 * Turns a failed charge into the sentence the customer reads AND whether that
 * sentence should keep Continue off (RET-1933).
 *
 * A decline gets its own copy per reason, falling back to a generic decline
 * message for reasons this app has no wording for yet — the reason set is
 * open on Stripe's side, so an unknown one must still say "your card was
 * declined" rather than "something went wrong". It does not block: decisión
 * congelada 1, arriba.
 *
 * A 400 gets its OWN copy too, and for the same reason a decline does: the
 * generic copy of the day — back then "vuelve a intentarlo en unos minutos" —
 * was a promise this repo cannot keep for it. A 400 means the amount itself was
 * rejected: retrying the same amount fails again, every time. It is also not the
 * card's fault, so it must not borrow the decline copy either — that would send
 * the customer to call their bank over a number they typed. It DOES block:
 * reintentar sin cambiar el monto no sirve de nada.
 *
 * (Ese texto ya no es el de `generic`: RET-2102 le quitó la ventana de tiempo.
 * Se cita como lo que motivó la llave propia de `amountRejected`, no como la
 * copia vigente.)
 *
 * Un desenlace DESCONOCIDO —sin `response`, o un status que el upstream
 * reserva para sus propias caídas (`>= 500`, o `424`, ver `isUnknownOutcome`)—
 * usa el genérico y no bloquea: decisión congelada 2, arriba.
 *
 * Cualquier OTRO 4xx con `response` —409, 404, 412...— es un rechazo
 * DEFINITIVO: el upstream sí contestó, y esa respuesta no cambia si el
 * cliente vuelve a apretar el mismo botón. Bloquea, y desde RET-2102 tiene su
 * propia llave (`definitiveRejection`) en vez de compartir la genérica.
 *
 * POR QUÉ SE PARTIÓ LA LLAVE (RET-2102). Es el mismo razonamiento con el que
 * `amountRejected` recibió copia propia —"el genérico «vuelve a intentarlo en
 * unos minutos» es una promesa que este repo no puede cumplir para él"—,
 * aplicado a las dos clases que seguían compartiéndola. `transportFailure`
 * deja Continuar VIVO y `definitiveRejection` lo APAGA: un solo texto no puede
 * ser honesto para los dos, y "inténtalo de nuevo" bajo un botón muerto
 * reproduce exactamente el callejón sin salida que RET-1933 acaba de quitar.
 * Sigue sin haber copy POR STATUS —`reason: "frozen"` es deliberadamente el
 * único que el 409 expone, ver `topup_controller.go`—; lo que hay ahora es
 * copy por lo que el cliente PUEDE HACER, que es lo que el botón ya decía.
 *
 * Y LA GENÉRICA DEJÓ DE PROMETER MINUTOS. Un 424 mezcla lo transitorio con lo
 * permanente y desde aquí no hay forma de separarlos (ver arriba), así que su
 * texto tiene que ser verdadero en los dos casos: ofrece reintentar Y una
 * segunda salida si el reintento no cura. El costo asumido es que el caso
 * transitorio pierde la pista de que esperar ayuda; se acepta porque la pista
 * falsa le cuesta más al caso permanente de lo que la verdadera le ahorra al
 * transitorio. Recuperarla pide el discriminador en el cuerpo del 424, que es
 * RET-2092.
 */
export function chargeErrorMessage(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
): tTopUpChargeErrorMessage {
  const classified = classify(error);

  switch (classified.kind) {
    case 'decline':
      return {
        text: t(`topUp.declines.${classified.reason}`, {
          defaultValue: t('topUp.declines.generic'),
        }),
        blocksContinue: false,
        // La única clase que habla de la tarjeta (RET-2348).
        clearsOnNewCard: true,
      };

    case 'amountRejected':
      return {
        text: t('topUp.errors.amountRejected'),
        blocksContinue: true,
        clearsOnNewCard: false,
      };

    case 'transportFailure':
      return {
        text: t('topUp.errors.generic'),
        blocksContinue: false,
        // Aquí NADIE sabe si se cobró. Una tarjeta nueva no lo aclara, y
        // quitar el aviso lo daría por resuelto.
        clearsOnNewCard: false,
      };

    case 'definitiveRejection':
      return {
        text: t('topUp.errors.definitiveRejection'),
        blocksContinue: true,
        clearsOnNewCard: false,
      };

    default: {
      const unhandled: never = classified;

      return unhandled;
    }
  }
}

/**
 * Razones de MÁQUINA que wallet-service manda en `response.data.reason` para
 * los rechazos de la ruta recurrente (RET-1935) — nunca prosa en inglés que
 * este archivo tendría que parsear. Mismo precedente que el `reason: "frozen"`
 * del cobro: el back las emite desde constantes tipadas
 * (`recurringDenialReason`, en `recurring_topup_controller.go`), y el BFF las
 * relaya intactas porque `relayableUpstreamStatus` sí pasa los 4xx.
 *
 * Constantes y no literales repetidos en cada `if`: con el literal suelto, un
 * typo en cualquiera de los dos lados —aquí o en el controller— degrada en
 * silencio a la copy genérica sin que nada lo marque.
 */
const RECURRING_REASON_ALREADY_ACTIVE = 'already_active';
const RECURRING_REASON_NOT_ACTIVE = 'not_active';
const RECURRING_REASON_ITEM_GONE = 'item_gone';

/**
 * Traduce el rechazo del alta o del reemplazo de la recarga automática a copy
 * ACCIONABLE, y decide si apaga Continuar (RET-1935 · C6, sobre el mecanismo
 * de RET-1933).
 *
 * POR QUÉ NO ES `chargeErrorMessage`. Ese clasificador ya trata cualquier 4xx
 * contestado como `definitiveRejection`: bloquea, y con el texto genérico.
 * Bloquear está BIEN aquí —las cuatro salidas de abajo son permanentes, y ésa
 * es exactamente la regla de RET-1933: se apaga sólo si reintentar daría lo
 * mismo—, pero el texto genérico no: le pide al cliente que reintente en unos
 * minutos una condición que no se cura con el tiempo. El ticket es
 * precisamente ese mensaje.
 *
 * Las tres razones conocidas tienen copy propio, y ninguna puede darse en la
 * otra operación: sólo el alta choca con "ya hay una activa", y sólo el
 * reemplazo puede fallar contra "no hay activa" o contra un renglón nuestro
 * que ya no existe.
 *
 *   404 + `not_active`     → ya no hay activa que reemplazar: "enciende una",
 *                             no "reintenta".
 *   409 + `already_active` → el alta chocó contra una que SÍ existe (el caso
 *                             exacto del ticket): manda a reemplazar.
 *   409 + `item_gone`      → la fila dice activa y no hay renglón vivo:
 *                             apagar y volver a encender, o soporte. Llave
 *                             propia porque la salida también lo es.
 *
 * Cualquier OTRO 409 de esta ruta —huérfano sin resolver, renglón ambiguo,
 * congelado, conflicto de moneda— es igual de permanente, pero la copy sí
 * depende de qué intentó el cliente: `isReplace` es la misma condición que
 * decidió qué llamar, así que el texto dice "activar" en el alta y
 * "actualizar" en el reemplazo. Sin eso, un huérfano pendiente en el ALTA
 * pintaba "no pudimos ACTUALIZAR" sobre una recarga que el cliente estaba
 * creando.
 *
 * LO QUE ESTA FUNCIÓN NO ARREGLA, y es deuda conocida: el 409 de
 * `ErrRecurringTopUpMigrationUnverified` viaja SIN `reason`, así que cae en la
 * rama de abajo y apaga Continuar — y es el único 409 de esta ruta donde
 * reintentar SÍ cura (el guard de precio del adapter reencuentra el renglón por
 * su metadata). Queda igual que hoy en staging, donde `definitiveRejection` ya
 * lo bloquea, así que no es una regresión; el arreglo de fondo es que ese 409
 * lleve su propia razón desde wallet-service.
 *
 * Todo lo demás —402 de tarjeta, 400 de monto, 424/5xx/red caída— se delega en
 * `chargeErrorMessage`, que ya sabe qué hacer y, sobre todo, ya sabe cuáles NO
 * deben apagar el botón.
 */
export function recurringErrorMessage(
  error: unknown,
  isReplace: boolean,
  t: (key: string, options?: Record<string, unknown>) => string,
): tTopUpChargeErrorMessage {
  const response = (
    error as { response?: { status?: number; data?: { reason?: string } } }
  )?.response;
  const reason = response?.data?.reason;

  // Ninguno de los rechazos propios de esta ruta habla de la tarjeta: son el
  // estado de la recarga automática, y guardar otra tarjeta no lo mueve
  // (RET-2348). Por eso los cuatro llevan `clearsOnNewCard: false`.
  if (response?.status === 404 && reason === RECURRING_REASON_NOT_ACTIVE) {
    return {
      text: t('topUp.errors.recurringNotActive'),
      blocksContinue: true,
      clearsOnNewCard: false,
    };
  }

  if (response?.status === 409) {
    if (reason === RECURRING_REASON_ALREADY_ACTIVE) {
      return {
        text: t('topUp.errors.recurringAlreadyActive'),
        blocksContinue: true,
        clearsOnNewCard: false,
      };
    }
    if (reason === RECURRING_REASON_ITEM_GONE) {
      return {
        text: t('topUp.errors.recurringItemGone'),
        blocksContinue: true,
        clearsOnNewCard: false,
      };
    }

    return {
      text: t(
        isReplace
          ? 'topUp.errors.recurringConflict'
          : 'topUp.errors.recurringEnableConflict',
      ),
      blocksContinue: true,
      clearsOnNewCard: false,
    };
  }

  return chargeErrorMessage(error, t);
}

// ── Recarga por saldo bajo (RET-2425, U6) ───────────────────────────────────

/** The 409 reasons of the threshold PUT that the modal resolves with the state. */
const THRESHOLD_REASON_RECURRING_ACTIVE = 'recurring_active';
const THRESHOLD_REASON_THRESHOLD_ACTIVE = 'threshold_active';
const THRESHOLD_REASON_PAST_DUE = 'past_due';
const THRESHOLD_REASON_PACKAGE_SCHEDULED = 'credit_package_scheduled';
const THRESHOLD_REASON_CANCELLATION = 'cancellation_scheduled';
const THRESHOLD_REASON_NO_WAPI = 'no_wapi';
const THRESHOLD_REASON_FROZEN = 'frozen';

/** The 400 codes that belong to the limit field; every other known code is the amount's. */
const THRESHOLD_FIELD_CODES = ['threshold_below_min'] as const;
const AMOUNT_FIELD_CODES = [
  'amount_below_min',
  'amount_above_max',
  'amount_not_above_threshold',
  'not_whole_units',
] as const;

/**
 * What a failed threshold PUT means for the modal (KTD7, the save flow in the
 * plan). The text is built here; WHAT TO DO next (re-read, open a
 * confirmation, close the attempt) is the container's, because it needs the
 * state and the network.
 *
 * - `message`: a sentence for the footer, with its class (RET-1933).
 * - `field`: a 400 with a known `code`, painted in the field the client check
 *   would paint, with the same sentence (`thresholdBoundText`). Wallet writes
 *   it as `{"error": "invalid_amount", "code", "limitMicros"}`, WITHOUT
 *   `reason` (`threshold_topup_controller.go`, `writeError`), and the service
 *   never copies `error`: the known `code` is the whole signal.
 * - `replace`: 409 `recurring_active` / `threshold_active`. Re-read and open the
 *   confirmation of the replacement.
 * - `typeBlocked`: 409 `past_due` / `credit_package_scheduled`. Re-read and
 *   show the notice of the blocked type (RC-22).
 * - `retention`: 409 `cancellation_scheduled`, wallet's backstop for PD-4 when
 *   the modal had no subscription data. Re-read and show the retention.
 * - `unknown`: 424, 5xx, no response, or a malformed 200. It does NOT say
 *   whether wallet charged or saved: the container re-reads the GET before it
 *   says anything.
 *
 * THE BODY `error` NEVER REACHES A TEXT (R17). `WalletRejectedError` does not
 * carry it, and the decline uses only `reason`.
 */
export type tThresholdSaveFailure =
  | { kind: 'message'; message: tTopUpChargeErrorMessage }
  | { kind: 'field'; field: 'threshold' | 'amount'; text: string }
  | { kind: 'replace' }
  | { kind: 'typeBlocked'; reason: tThresholdBlockedBy }
  | { kind: 'retention' }
  | { kind: 'unknown' };

/**
 * The generic text of the threshold top-up: it says the configuration was not
 * saved, and offers a retry AND a second way out. It does not block, so it is
 * honest for a transient failure and for one that persists.
 *
 * Only for an answer that says nothing was saved. An outcome nobody knows (a
 * 424, a 5xx, a dropped connection with no re-read) uses the one-off's
 * unknown-outcome text instead (`chargeErrorMessage`).
 */
export function thresholdSaveGenericMessage(
  t: tTranslateFn,
): tTopUpChargeErrorMessage {
  return {
    text: t('topUp.errors.thresholdSave'),
    blocksContinue: false,
    clearsOnNewCard: false,
  };
}

type tTranslateFn = (key: string, options?: Record<string, unknown>) => string;

export function thresholdSaveFailure(
  error: unknown,
  context: { currency: tWalletCurrency; locale: string; t: tTranslateFn },
): tThresholdSaveFailure {
  const { currency, locale, t } = context;

  // 402: the bank's reason, plus «Tu recarga automática no se guardó.»
  // (RC-20). It does not block: the attempt closes on a 402, so the retry
  // reaches the bank with a new key.
  if (error instanceof WalletDeclinedError) {
    const decline = t(`topUp.declines.${error.decline.reason}`, {
      defaultValue: t('topUp.declines.generic'),
    });

    return {
      kind: 'message',
      message: {
        text: `${decline} ${t('topUp.threshold.notSaved')}`,
        blocksContinue: false,
        clearsOnNewCard: true,
      },
    };
  }

  if (error instanceof WalletRejectedError) {
    if (error.status === 400) {
      const code = error.code;

      if (code) {
        const thresholdCode = THRESHOLD_FIELD_CODES.find(
          (known) => known === code,
        );
        const amountCode = AMOUNT_FIELD_CODES.find((known) => known === code);
        const knownCode = thresholdCode ?? amountCode;

        if (knownCode) {
          return {
            kind: 'field',
            field: thresholdCode ? 'threshold' : 'amount',
            text: thresholdBoundText({
              code: knownCode,
              limitMicros: error.limitMicros,
              currency,
              locale,
              t,
            }),
          };
        }
      }

      // A 400 without a code this front knows (a reused key, a body the BFF
      // rejected): nothing was saved and the attempt closed, so a retry goes
      // with a new key.
      return { kind: 'message', message: thresholdSaveGenericMessage(t) };
    }

    switch (error.reason) {
      case THRESHOLD_REASON_RECURRING_ACTIVE:
      case THRESHOLD_REASON_THRESHOLD_ACTIVE:
        return { kind: 'replace' };
      case THRESHOLD_REASON_PAST_DUE:
        return { kind: 'typeBlocked', reason: 'past_due' };
      case THRESHOLD_REASON_PACKAGE_SCHEDULED:
        return { kind: 'typeBlocked', reason: 'credit_package_scheduled' };
      case THRESHOLD_REASON_CANCELLATION:
        return { kind: 'retention' };
      case THRESHOLD_REASON_NO_WAPI:
      case THRESHOLD_REASON_FROZEN:
        // RC-15 (`no_wapi`) and `DenialFrozen` (`frozen`): the same text and
        // class the one-off top-up shows for these 409s today
        // (`chargeErrorMessage` → `definitiveRejection`). It blocks: nothing
        // changes by pressing again.
        return {
          kind: 'message',
          message: {
            text: t('topUp.errors.definitiveRejection'),
            blocksContinue: true,
            clearsOnNewCard: false,
          },
        };
      default:
        // A 409 without a reason (several payment customers, currency) or one
        // this front does not know.
        return { kind: 'message', message: thresholdSaveGenericMessage(t) };
    }
  }

  if (isUnknownOutcome(error)) return { kind: 'unknown' };

  // Any other answer (a 403 because the role changed with the modal open, a
  // 404): wallet answered and saved nothing.
  return { kind: 'message', message: thresholdSaveGenericMessage(t) };
}

/**
 * The 409 reasons of the recurring POST that concern the threshold top-up
 * (RC-22), read from the raw response: `enableRecurringTopUp` rethrows the
 * axios error as it is. `null` for anything else, which keeps today's path
 * (`recurringErrorMessage`).
 */
export function recurringThresholdConflict(
  error: unknown,
): 'threshold_active' | 'cancellation_scheduled' | tThresholdBlockedBy | null {
  const response = (
    error as { response?: { status?: number; data?: { reason?: unknown } } }
  )?.response;

  if (response?.status !== 409) return null;

  switch (response.data?.reason) {
    case THRESHOLD_REASON_THRESHOLD_ACTIVE:
      return 'threshold_active';
    case THRESHOLD_REASON_CANCELLATION:
      return 'cancellation_scheduled';
    case THRESHOLD_REASON_PAST_DUE:
      return 'past_due';
    case THRESHOLD_REASON_PACKAGE_SCHEDULED:
      return 'credit_package_scheduled';
    default:
      return null;
  }
}
